import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Upload,
  Camera,
  FileText,
  Plus,
  Eye,
  Check,
  RefreshCw,
  Mail,
  Search,
  Trash2,
  AlertTriangle,
  Paperclip,
  ArrowUpDown,
} from "lucide-react";
import { apiFetch, API } from "@/utils/api";
import { getInvoiceDefaults } from "@/utils/companyProfile";
import ReconciliationLinkDialog from "@/components/ReconciliationLinkDialog";
import AssignmentCell from "../components/AssignmentCell";
import InvoicePreviewDialog from "@/components/InvoicePreviewDialog";
import OCRPreviewDialog from "@/components/OCRPreviewDialog";
import SupplierCategorySuggestion from "@/components/SupplierCategorySuggestion";
import { useSupplierCategories } from "@/hooks/useSupplierCategories";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileInvoices from "@/mobile/MobileInvoices";

function formatCurrency(amount, currency = "EUR") {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency,
  }).format(amount);
}

function isForeignInvoice(inv) {
  return inv.currency && inv.currency !== "EUR";
}

function isReconciled(inv) {
  return inv.total_amount_eur != null;
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-ES");
}

function StatusBadge({ status }) {
  // Estados reales del flujo: pending -> accounted. El estado intermedio
  // "validated" se eliminó (roadmap 1.4: nunca fue alcanzable desde la UI).
  const styles = {
    pending: "bg-amber-100 text-amber-700",
    accounted: "bg-blue-100 text-blue-700",
    paid: "bg-slate-100 text-slate-600",
  };

  const labels = {
    pending: "Pendiente",
    accounted: "Contabilizada",
    paid: "Pagada",
  };

  return (
    <Badge className={styles[status] || styles.pending} variant="secondary">
      {labels[status] || status}
    </Badge>
  );
}

function PaymentBadge({ status }) {
  const styles = {
    unpaid: "bg-rose-100 text-rose-700",
    partial: "bg-amber-100 text-amber-700",
    paid: "bg-emerald-100 text-emerald-700",
    overpaid: "bg-purple-100 text-purple-700",
  };

  const labels = {
    unpaid: "Sin pagar",
    partial: "Parcial",
    paid: "Pagada",
    overpaid: "Sobrepagada",
  };

  return (
    <Badge className={styles[status] || styles.unpaid} variant="secondary">
      {labels[status] || status}
    </Badge>
  );
}

export function InvoiceForm({ invoice, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    supplier_name: invoice?.supplier_name || "",
    supplier_nif: invoice?.supplier_nif || "",
    supplier_type: invoice?.supplier_type || "nacional",
    invoice_number: invoice?.invoice_number || "",
    invoice_date: invoice?.invoice_date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
    due_date: invoice?.due_date?.slice(0, 10) || "",
    base_amount: invoice?.base_amount ?? "",
    vat_rate: invoice?.vat_rate ?? 21,
    irpf_rate: invoice?.irpf_rate ?? 0,
    original_currency: invoice?.original_currency || invoice?.currency || "EUR",
    original_amount: invoice?.original_amount ?? "",
    currency: invoice?.currency || "EUR",
    exchange_rate: invoice?.exchange_rate ?? 1.0,
    category: invoice?.category || "",
    account_code: invoice?.account_code || "629",
    vat_reverse_charge: invoice?.vat_reverse_charge ?? false,
    vat_non_deductible: invoice?.vat_non_deductible || false,
  });
  // Al editar, arranca con la categoría que el GET ya trae por JOIN
  // (invoice.category); en alta queda vacía hasta que se elija o la proponga
  // el histórico/IA (smoke 30-08-2026, B1).
  const [supplierCategory, setSupplierCategory] = useState(invoice?.category || "");
  // 3.6: catálogo editable del tenant + propuesta con fuente (patrón 3.4)
  const supplierCategories = useSupplierCategories();
  const [categoryProposal, setCategoryProposal] = useState(null);

  // Estado de integridad contable (roadmap §1.2 bloque 2):
  // - sellada: incluida en una declaración presentada → campos contables bloqueados
  // - fxFixed: divisa con EUR real fijado por el banco → EUR inmutable, tipo implícito derivado
  // - hasPayments: pagos conciliados → al guardar se recalcula el estado de pago
  const sealed = Boolean(invoice?.filed_period);
  const fxFixed = Boolean(
    invoice && isForeignInvoice(invoice) && invoice.total_amount_eur != null
    && (invoice.amount_paid || 0) > 0
  );
  const hasPayments = Boolean(invoice && (invoice.amount_paid || 0) > 0);

  // Errores de campos obligatorios: {campo: true}. Se rellenan en submit y se limpian al corregir cada campo.
  const [fieldErrors, setFieldErrors] = useState({});

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Factura NUEVA: el IVA por defecto sale del perfil de empresa (el IRPF de una
  // recibida es la retención del proveedor autónomo — por defecto 0, se indica a mano).
  // Al editar (invoice existe) se conservan los valores de la factura.
  useEffect(() => {
    if (invoice) return;
    let cancelled = false;
    getInvoiceDefaults().then((d) => {
      if (cancelled) return;
      setFormData((prev) =>
        // Solo si el usuario no ha tocado aún el IVA (sigue en el literal inicial)
        prev.vat_rate === 21 ? { ...prev, vat_rate: d.vat_rate } : prev
      );
    });
    return () => {
      cancelled = true;
    };
  }, [invoice]);

  // Pre-rellena la categoría cuando el proveedor se confirma (al salir del campo).
  // ai=true (3.6): sin señal determinista y con consentimiento del tenant, el
  // backend pregunta a la IA; la fuente se muestra en la línea bajo el select.
  const handleSupplierBlur = async () => {
    const name = formData.supplier_name?.trim();
    if (!name || supplierCategory) return;
    try {
      const res = await apiFetch(`${API}/supplier-categories/propose?supplier_name=${encodeURIComponent(name)}&ai=true`);
      if (res.ok) {
        const data = await res.json();
        if (data.category) {
          setSupplierCategory(data.category);
          setCategoryProposal(data);
        }
      }
    } catch (_) { /* silencioso */ }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (sealed) {
      // Solo los campos sin efecto contable: el resto está bloqueado por el backend.
      onSave({
        category: formData.category,
        due_date: formData.due_date,
        _supplier_category: supplierCategory || null,
      });
      return;
    }
    // base_amount = 0 es válido (facturas de importe 0); solo se bloquea si está vacío.
    const missing = {};
    if (!formData.supplier_name) missing.supplier_name = true;
    if (!formData.invoice_number) missing.invoice_number = true;
    if (formData.base_amount === "" || formData.base_amount == null) missing.base_amount = true;
    if (Object.keys(missing).length > 0) {
      const labels = {
        supplier_name: "Proveedor",
        invoice_number: "Nº Factura",
        base_amount: "Base Imponible",
      };
      setFieldErrors(missing);
      toast.error(`Faltan campos obligatorios: ${Object.keys(missing).map((k) => labels[k]).join(", ")}`);
      // Scroll al primer campo con error dentro del diálogo
      setTimeout(() => {
        document.querySelector('[data-field-error="true"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    setFieldErrors({});
    onSave({ ...formData, _supplier_category: supplierCategory || null });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {sealed && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="sealed-warning">
          <p className="font-medium">Factura incluida en una declaración ya presentada ({invoice.filed_period})</p>
          <p className="mt-1">
            Modificar sus datos contables puede requerir una declaración complementaria
            o sustitutiva ante la AEAT; consúltalo con tu asesor si lo tienes. Los campos
            con efecto contable están bloqueados: si aun así necesitas cambiarlos, primero
            deshaz el sellado del periodo (Fiscal → Deshacer presentado).
          </p>
        </div>
      )}
      {!sealed && fxFixed && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800" data-testid="fx-fixed-warning">
          <p className="font-medium">Factura en divisa ya conciliada</p>
          <p className="mt-1">
            El importe en euros ({formatCurrency(invoice.total_amount_eur)}) lo fijó el banco
            y no cambia. Si editas los importes en divisa se recalculará el tipo de cambio
            implícito. Si el pago conciliado no corresponde a esta factura, el camino es
            deshacer la conciliación (Conciliación → Historial), no seguir editando.
          </p>
        </div>
      )}
      {!sealed && !fxFixed && hasPayments && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="payments-warning">
          <p className="font-medium">Esta factura tiene pagos conciliados ({formatCurrency(invoice.amount_paid)})</p>
          <p className="mt-1">
            Puedes editarla y su asiento se recalculará, pero el importe pagado no cambia
            (es dinero real del banco): el estado pasará a parcial, pagada o sobrepagada
            según el nuevo total.
          </p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div data-field-error={fieldErrors.supplier_name ? "true" : undefined}>
          <Label>Proveedor *</Label>
          <Input
            value={formData.supplier_name}
            onChange={(e) => {
              setFormData({ ...formData, supplier_name: e.target.value });
              setSupplierCategory("");
              setCategoryProposal(null);
              clearFieldError("supplier_name");
            }}
            onBlur={handleSupplierBlur}
            placeholder="Nombre del proveedor"
            data-testid="invoice-supplier-input"
            disabled={sealed}
            className={fieldErrors.supplier_name ? "border-red-500 focus-visible:ring-red-500" : undefined}
          />
          {fieldErrors.supplier_name && (
            <p className="text-xs text-red-500 mt-1">Este campo es obligatorio</p>
          )}
        </div>
        <div>
          <Label>NIF</Label>
          <Input
            value={formData.supplier_nif}
            onChange={(e) => setFormData({ ...formData, supplier_nif: e.target.value })}
            placeholder="B12345678"
            data-testid="invoice-nif-input"
            disabled={sealed}
          />
        </div>
      </div>

      <div>
        <Label>Categoría del proveedor</Label>
        <Select
          value={supplierCategory}
          onValueChange={(value) => setSupplierCategory(value)}
        >
          <SelectTrigger data-testid="invoice-supplier-category-select">
            <SelectValue placeholder="Selecciona una categoría..." />
          </SelectTrigger>
          <SelectContent>
            {/* Si la propuesta (histórico) no está en el catálogo cargado,
                se añade como opción para que el select nunca quede mudo */}
            {(supplierCategory && !supplierCategories.includes(supplierCategory)
              ? [...supplierCategories, supplierCategory]
              : supplierCategories
            ).map((cat) => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <SupplierCategorySuggestion proposal={categoryProposal} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Tipo de proveedor</Label>
          <Select
            value={formData.supplier_type}
            disabled={sealed}
            onValueChange={(value) => {
              const updates = { supplier_type: value };
              const isForeign = value !== "nacional";
              updates.vat_reverse_charge = isForeign;
              if (isForeign) {
                updates.vat_rate = 0;
              } else if (formData.vat_rate === 0) {
                updates.vat_rate = 21;
              }
              setFormData({ ...formData, ...updates });
            }}
          >
            <SelectTrigger data-testid="invoice-supplier-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="nacional">Nacional</SelectItem>
              <SelectItem value="intracomunitario">Intracomunitario</SelectItem>
              <SelectItem value="extracomunitario">Extracomunitario</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Divisa</Label>
          <Select
            value={formData.original_currency}
            disabled={sealed}
            onValueChange={(value) => setFormData({ ...formData, original_currency: value })}
          >
            <SelectTrigger data-testid="invoice-original-currency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="EUR">EUR</SelectItem>
              <SelectItem value="USD">USD</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {formData.supplier_type !== "nacional" && (
        <div className="flex items-center gap-2">
          <Badge className="bg-amber-100 text-amber-700 border-amber-200" variant="secondary">
            Inversion sujeto pasivo
          </Badge>
          {formData.base_amount && (
            <span className="text-sm text-slate-500">
              IVA {formData.vat_rate}%: {formatCurrency((parseFloat(formData.base_amount) || 0) * formData.vat_rate / 100)}
            </span>
          )}
        </div>
      )}

      <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
        <Checkbox
          id="vat-non-deductible"
          checked={!!formData.vat_non_deductible}
          disabled={sealed}
          onCheckedChange={(checked) => setFormData({ ...formData, vat_non_deductible: checked === true })}
          data-testid="invoice-vat-non-deductible"
          className="mt-0.5"
        />
        <div className="space-y-1">
          <Label htmlFor="vat-non-deductible" className="cursor-pointer font-medium">
            IVA no deducible
          </Label>
          <p className="text-xs text-slate-500">
            Marca esta opción si el proveedor te cobró IVA que no puedes deducir (p.ej. factura UE cuando aún no estabas en VIES). El importe íntegro irá a gasto y no se registrará IVA soportado.
          </p>
        </div>
      </div>

      {formData.original_currency === "USD" && (
        <div>
          <Label>Importe original (USD)</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.original_amount}
            onChange={(e) => setFormData({ ...formData, original_amount: e.target.value === "" ? "" : parseFloat(e.target.value) })}
            placeholder="0.00"
            data-testid="invoice-original-amount-input"
            disabled={sealed}
          />
          <p className="text-xs text-slate-500 mt-1">
            {fxFixed
              ? "EUR ya fijado por el banco: al cambiar este importe se recalcula el tipo de cambio implícito"
              : "El tipo de cambio real se calculara al conciliar"}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div data-field-error={fieldErrors.invoice_number ? "true" : undefined}>
          <Label>Nº Factura *</Label>
          <Input
            value={formData.invoice_number}
            onChange={(e) => {
              setFormData({ ...formData, invoice_number: e.target.value });
              clearFieldError("invoice_number");
            }}
            placeholder="FAC-001"
            data-testid="invoice-number-input"
            disabled={sealed}
            className={fieldErrors.invoice_number ? "border-red-500 focus-visible:ring-red-500" : undefined}
          />
          {fieldErrors.invoice_number && (
            <p className="text-xs text-red-500 mt-1">Este campo es obligatorio</p>
          )}
        </div>
        <div>
          <Label>Fecha Factura *</Label>
          <Input
            type="date"
            value={formData.invoice_date}
            onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
            data-testid="invoice-date-input"
            disabled={sealed}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div data-field-error={fieldErrors.base_amount ? "true" : undefined}>
          <Label>{formData.original_currency && formData.original_currency !== "EUR" ? `Base Imponible (${formData.original_currency}) *` : "Base Imponible *"}</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.base_amount}
            onChange={(e) => {
              setFormData({ ...formData, base_amount: e.target.value === "" ? "" : parseFloat(e.target.value) });
              clearFieldError("base_amount");
            }}
            placeholder="0.00"
            data-testid="invoice-base-input"
            disabled={sealed}
            className={fieldErrors.base_amount ? "border-red-500 focus-visible:ring-red-500" : undefined}
          />
          {fieldErrors.base_amount && (
            <p className="text-xs text-red-500 mt-1">Este campo es obligatorio</p>
          )}
        </div>
        <div>
          <Label>Tipo IVA (%)</Label>
          <Select
            value={String(formData.vat_rate)}
            disabled={sealed}
            onValueChange={(value) => setFormData({ ...formData, vat_rate: parseFloat(value) })}
          >
            <SelectTrigger data-testid="invoice-vat-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">0%</SelectItem>
              <SelectItem value="4">4%</SelectItem>
              <SelectItem value="10">10%</SelectItem>
              <SelectItem value="21">21%</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {formData.supplier_type === "nacional" && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Retención IRPF (%)</Label>
            <Input
              type="number"
              step="0.5"
              min="0"
              max="60"
              value={formData.irpf_rate}
              onChange={(e) => setFormData({ ...formData, irpf_rate: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
              placeholder="0"
              data-testid="invoice-irpf-input"
              disabled={sealed}
            />
            <p className="text-xs text-slate-500 mt-1">
              Solo si el proveedor (autónomo) la aplica en su factura: 7%, 15% o el que indique. Se descuenta del total a pagar y se ingresa a Hacienda (modelo 111).
            </p>
          </div>
          {(parseFloat(formData.irpf_rate) || 0) > 0 && formData.base_amount !== "" && (
            <div className="flex items-start pt-7">
              <p className="text-sm text-slate-500" data-testid="invoice-irpf-summary">
                IRPF −{formatCurrency((parseFloat(formData.base_amount) || 0) * formData.irpf_rate / 100)}
                {" → total "}
                {formatCurrency((parseFloat(formData.base_amount) || 0) * (1 + formData.vat_rate / 100 - formData.irpf_rate / 100))}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Fecha Vencimiento</Label>
          <Input
            type="date"
            value={formData.due_date}
            onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
            data-testid="invoice-due-input"
          />
        </div>
        <div>
          <Label>Cuenta Contable</Label>
          <Select
            value={formData.account_code}
            disabled={sealed}
            onValueChange={(value) => setFormData({ ...formData, account_code: value })}
          >
            <SelectTrigger data-testid="invoice-account-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="621">621 - Arrendamientos</SelectItem>
              <SelectItem value="622">622 - Reparaciones</SelectItem>
              <SelectItem value="623">623 - Profesionales</SelectItem>
              <SelectItem value="625">625 - Seguros</SelectItem>
              <SelectItem value="626">626 - Bancarios</SelectItem>
              <SelectItem value="627">627 - Publicidad</SelectItem>
              <SelectItem value="628">628 - Suministros</SelectItem>
              <SelectItem value="629">629 - Otros servicios</SelectItem>
              <SelectItem value="600">600 - Compras mercaderías</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" data-testid="save-invoice-btn">
          Guardar
        </Button>
      </DialogFooter>
    </form>
  );
}

function InvoicesReceivedDesktop() {
  const [invoices, setInvoices] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ocrQueue, setOcrQueue] = useState([]);
  const currentOcrDoc = ocrQueue[0] || null;
  const ocrDialogOpen = ocrQueue.length > 0;
  const popOcrQueue = () => setOcrQueue(q => q.slice(1));
  // Ingesta por correo: estado del lector + bandeja de pendientes
  const [emailIngest, setEmailIngest] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [expenseSplits, setExpenseSplits] = useState({});
  // 3.3 — sugerencias de reparto (mapa invoice_id → sugerencia | null).
  // Los null también se guardan para no repetir la llamada.
  const [splitSuggestions, setSplitSuggestions] = useState({});
  const [filterUnassigned, setFilterUnassigned] = useState(false);
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("asc"); // asc = más antiguas primero
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [totalInvoices, setTotalInvoices] = useState(0);
  const pdfInputRef = useRef(null);
  const [uploadingPdfId, setUploadingPdfId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const handlePdfUpload = async (invoiceId, file) => {
    if (!file) return;
    setUploadingPdfId(invoiceId);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await apiFetch(`${API}/invoices/received/${invoiceId}/upload-pdf`, {
        method: "POST",
        body: formData,
      });
      if (response.ok) {
        toast.success("PDF adjuntado correctamente");
        fetchInvoices();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Error al subir PDF");
      }
    } catch (error) {
      toast.error("Error al subir PDF");
    } finally {
      setUploadingPdfId(null);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const fetchClients = async () => {
    try {
      const res = await apiFetch(`${API}/crm/clients`);
      if (res.ok) setClients(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchServices = async (clientId) => {
    try {
      const url = clientId ? `${API}/crm/services?client_id=${clientId}` : `${API}/crm/services`;
      const res = await apiFetch(url);
      if (res.ok) setServices(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchExpenseSplits = async () => {
    try {
      const res = await apiFetch(`${API}/crm/expenses/splits?expense_type=invoice_received`);
      if (!res.ok) return;
      const data = await res.json();
      const map = {};
      data.forEach(s => { (map[s.expense_id] ||= []).push(s); });
      Object.values(map).forEach(arr => arr.sort((a, b) => a.line_index - b.line_index));
      setExpenseSplits(map);
    } catch (e) { console.error(e); }
  };

  const fetchInvoices = async () => {
    try {
      const params = new URLSearchParams({ limit: "1000" });
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (search) params.append("search", search);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      const response = await apiFetch(`${API}/invoices/received?${params}`);
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
        setTotalInvoices(data.total || (data.invoices || []).length);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await apiFetch(`${API}/documents?status=completed`);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // ---- Ingesta por correo (spec 2026-07-03): estado del lector y bandeja ----
  const fetchEmailIngestStatus = async () => {
    try {
      const r = await apiFetch(`${API}/ingest/email/status`);
      if (r.ok) setEmailIngest(await r.json());
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // «Revisar»: carga los PDF del correo aún sin factura en la MISMA cola OCR que una subida manual
  const handleReviewEmailDocs = async () => {
    try {
      const r = await apiFetch(`${API}/documents?source=email&unlinked=true`);
      if (!r.ok) return;
      const docs = (await r.json()).documents || [];
      const ready = docs.filter((d) => d.ocr_status === "completed");
      const failed = docs.length - ready.length;
      if (ready.length === 0 && failed === 0) {
        toast.info("No hay facturas del correo pendientes");
        return;
      }
      if (failed > 0) {
        toast.warning(`${failed} PDF del correo con OCR fallido: revísalos en Documentos`);
      }
      setOcrQueue((q) => [...q, ...ready.filter((d) => !q.some((x) => x.document_id === d.document_id))]);
    } catch (error) {
      toast.error("No se pudo cargar la bandeja del correo");
    }
  };

  const handleCheckEmailNow = async () => {
    setCheckingEmail(true);
    try {
      const r = await apiFetch(`${API}/ingest/email/run`, { method: "POST" });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(body.detail || "No se pudo comprobar el correo", { duration: 8000 });
      } else if (body.error) {
        toast.error(`Correo: ${body.error}`, { duration: 10000 });
      } else {
        const n = body.documents_created ?? 0;
        const r = body.receipts_skipped ?? 0;
        const extra = r > 0 ? ` (${r} recibo(s) ignorado(s))` : "";
        toast.success(n > 0 ? `${n} factura(s) nueva(s) capturada(s) del correo${extra}` : `Correo comprobado: nada nuevo${extra}`);
      }
      await Promise.all([fetchEmailIngestStatus(), fetchDocuments()]);
    } catch (error) {
      toast.error("No se pudo comprobar el correo");
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleDiscardEmailDoc = async (documentId) => {
    try {
      const r = await apiFetch(`${API}/documents/${documentId}`, { method: "DELETE" });
      if (r.ok) {
        toast.success("Documento descartado");
        popOcrQueue();
        fetchDocuments();
        fetchEmailIngestStatus();
      } else {
        toast.error("No se pudo descartar el documento");
      }
    } catch (error) {
      toast.error("No se pudo descartar el documento");
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchDocuments();
    fetchEmailIngestStatus();
    fetchClients();
    fetchServices();
    fetchExpenseSplits();
  }, [statusFilter, startDate, endDate]);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await apiFetch(`${API}/documents/upload`, {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          const doc = await response.json();
          if (doc.ocr_status === "completed") {
            setOcrQueue(q => [...q, doc]);
          } else {
            toast.info(`${file.name}: subido, procesando OCR...`);
          }
          fetchDocuments();
        } else {
          // El backend explica el rechazo (imagen sin consentimiento IA,
          // formato no soportado, archivo corrupto...) — mostrarlo tal cual.
          const err = await response.json().catch(() => ({}));
          toast.error(err.detail || `Error al subir ${file.name}`, { duration: 8000 });
        }
      } catch (error) {
        toast.error(`Error al subir ${file.name}`);
      }
    }
    setUploading(false);
    if (files.length > 1) {
      toast.info(`${files.length} archivos subidos. Revisa el OCR de cada uno cuando esté listo.`);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleCreateFromOCR = async (documentId, supplierCategory) => {
    try {
      const response = await apiFetch(`${API}/documents/${documentId}/create-invoice`, {
        method: "POST",
      });

      if (response.ok) {
        const invoice = await response.json();
        // Guardar categoría del proveedor si se ha seleccionado una
        if (supplierCategory && invoice.supplier_name) {
          apiFetch(`${API}/supplier-categories`, {
            method: "PUT",
            body: JSON.stringify({
              supplier_name: invoice.supplier_name,
              category: supplierCategory,
            }),
          }).catch(() => { /* silencioso */ });
        }
        if (invoice.is_duplicate) {
          toast.warning("Posible factura duplicada detectada");
        } else {
          toast.success("Factura creada desde OCR");
        }
        popOcrQueue();
        fetchInvoices();
        fetchEmailIngestStatus();
      }
    } catch (error) {
      toast.error("Error al crear factura");
    }
  };

  const handleSaveInvoice = async (formData) => {
    try {
      const { _supplier_category, ...invoicePayload } = formData;
      const url = editingInvoice
        ? `${API}/invoices/received/${editingInvoice.invoice_id}`
        : `${API}/invoices/received`;

      const response = await apiFetch(url, {
        method: editingInvoice ? "PUT" : "POST",
        body: JSON.stringify(invoicePayload),
      });

      if (response.ok) {
        // Guardar categoría del proveedor si se ha seleccionado una
        if (_supplier_category && invoicePayload.supplier_name) {
          apiFetch(`${API}/supplier-categories`, {
            method: "PUT",
            body: JSON.stringify({
              supplier_name: invoicePayload.supplier_name,
              category: _supplier_category,
            }),
          }).catch(() => { /* silencioso — no bloquea el guardado de factura */ });
        }
        const body = await response.json().catch(() => ({}));
        toast.success(editingInvoice ? "Factura actualizada" : "Factura creada");
        // Integridad §1.2: informar del recálculo tras editar con pagos conciliados
        if (editingInvoice && body.reaccounted) {
          toast.info("Asiento contable recalculado con los nuevos importes");
        }
        if (editingInvoice && body.payment_status === "overpaid") {
          toast.warning(
            "La factura queda SOBREPAGADA: los pagos conciliados superan el nuevo total. " +
            "Revisa si la conciliación corresponde a esta factura."
          );
        }
        setDialogOpen(false);
        setEditingInvoice(null);
        fetchInvoices();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Error al guardar factura", { duration: 9000 });
      }
    } catch (error) {
      toast.error("Error al guardar factura");
    }
  };

  const handleAccountInvoice = async (invoiceId) => {
    try {
      const response = await apiFetch(`${API}/invoices/received/${invoiceId}/account`, {
        method: "POST",
      });

      if (response.ok) {
        toast.success("Factura contabilizada");
        fetchInvoices();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al contabilizar");
      }
    } catch (error) {
      toast.error("Error al contabilizar");
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!confirm("¿Eliminar esta factura?")) return;

    try {
      const response = await apiFetch(`${API}/invoices/received/${invoiceId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        const body = await response.json().catch(() => ({}));
        toast.success(
          body.reverted_entries
            ? "Factura eliminada y su asiento revertido"
            : "Factura eliminada"
        );
        fetchInvoices();
      } else {
        // 409: conciliada o sellada — el detalle explica el camino a seguir
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Error al eliminar", { duration: 9000 });
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // 3.3 — fetch perezoso de la sugerencia de reparto al abrir el popover:
  // solo si la factura no tiene split y aún no se pidió para ese id.
  // Si consent=false y no hay histórico, el backend devuelve null y aquí
  // no se pinta nada (cero avisos).
  const fetchSplitSuggestion = async (expenseId) => {
    if (expenseSplits[expenseId]?.length) return;
    if (expenseId in splitSuggestions) return;
    try {
      const res = await apiFetch(`${API}/crm/expenses/split-suggestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ expense_ids: [expenseId] }),
      });
      if (!res.ok) return;
      const data = await res.json();
      setSplitSuggestions(prev => ({
        ...prev,
        [expenseId]: data.suggestions?.[expenseId] ?? null,
      }));
    } catch {
      // Sin sugerencia no pasa nada: el reparto manual sigue intacto.
    }
  };

  const saveSplits = async (invoiceId, payload) => {
    const res = await apiFetch(`${API}/crm/expenses/invoice_received/${invoiceId}/splits`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      toast.error(`Error: ${err.detail || res.statusText}`);
      return;
    }
    const updated = await res.json();
    setExpenseSplits(prev => ({ ...prev, [invoiceId]: updated }));
    toast.success("Asignación guardada");
  };

  const deleteSplits = async (invoiceId) => {
    const res = await apiFetch(`${API}/crm/expenses/invoice_received/${invoiceId}/splits`, {
      method: "DELETE",
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      toast.error(`Error: ${err.detail || res.statusText}`);
      return;
    }
    setExpenseSplits(prev => {
      const { [invoiceId]: _, ...rest } = prev;
      return rest;
    });
    // 3.3 — al quitar el split, olvidar la sugerencia cacheada (el backend
    // también invalida la suya): al reabrir se pedirá una fresca.
    setSplitSuggestions(prev => {
      const { [invoiceId]: _, ...rest } = prev;
      return rest;
    });
    toast.success("Asignación eliminada");
  };

  // Sort local por fecha de factura (el backend siempre devuelve DESC)
  const sortedInvoices = [...invoices].sort((a, b) => {
    const da = a.invoice_date || "";
    const db = b.invoice_date || "";
    if (sortOrder === "asc") return da.localeCompare(db);
    return db.localeCompare(da);
  });

  // Lista de proveedores únicos para el dropdown (orden alfabético es)
  const supplierOptions = Array.from(
    new Set(invoices.map(inv => inv.supplier_name).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, "es"));

  // Aplicar filtro de proveedor antes que el de "sin asignar"
  const filteredBySupplier = supplierFilter === "all"
    ? sortedInvoices
    : sortedInvoices.filter(inv => inv.supplier_name === supplierFilter);
  const unassignedInvoices = filteredBySupplier.filter(inv => !expenseSplits[inv.invoice_id]);
  // Solo se suman en EUR las facturas conciliadas (o EUR nativas).
  // Las extranjeras sin conciliar se cuentan aparte — su importe real se conocerá al conciliar con banco.
  const unassignedEurSum = unassignedInvoices
    .filter(inv => !isForeignInvoice(inv) || isReconciled(inv))
    .reduce((sum, inv) => sum + (inv.total_amount_eur ?? inv.total_amount ?? 0), 0);
  const unassignedPendingReconciliation = unassignedInvoices.filter(
    inv => isForeignInvoice(inv) && !isReconciled(inv)
  );

  // Selección masiva para contabilizar facturas pendientes en bloque.
  const visibleInvoices = filterUnassigned ? unassignedInvoices : filteredBySupplier;
  const pendingVisible = visibleInvoices.filter(inv => inv.status === "pending");
  const allPendingSelected = pendingVisible.length > 0 && pendingVisible.every(inv => selectedIds.has(inv.invoice_id));

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAllPending = () => {
    setSelectedIds(allPendingSelected ? new Set() : new Set(pendingVisible.map(i => i.invoice_id)));
  };
  const handleBulkAccount = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const response = await apiFetch(`${API}/invoices/received/account-bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ invoice_ids: ids }),
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Facturas contabilizadas");
        if (data.failed?.length) toast.error(`${data.failed.length} no se pudieron contabilizar`);
        setSelectedIds(new Set());
        fetchInvoices();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al contabilizar");
      }
    } catch (e) {
      toast.error("Error al contabilizar en bloque");
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-received-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Facturas Recibidas
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Gestiona las facturas de compras y gastos
          </p>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <input
            type="file"
            ref={fileInputRef}
            onChange={(e) => handleFileUpload(Array.from(e.target.files || []))}
            accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif"
            className="hidden"
            multiple
          />
          <input
            type="file"
            ref={pdfInputRef}
            onChange={(e) => {
              const file = e.target.files?.[0];
              const invoiceId = pdfInputRef.current?.dataset.invoiceId;
              if (file && invoiceId) handlePdfUpload(invoiceId, file);
            }}
            accept=".pdf"
            className="hidden"
          />
          <input
            type="file"
            ref={cameraInputRef}
            onChange={(e) => handleFileUpload(Array.from(e.target.files || []))}
            accept="image/*"
            capture="environment"
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-pdf-btn"
          >
            {uploading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Subir factura
          </Button>
          <Button
            variant="outline"
            className="lg:hidden"
            onClick={() => cameraInputRef.current?.click()}
            disabled={uploading}
            data-testid="camera-upload-btn"
          >
            <Camera className="w-4 h-4 mr-2" />
            Hacer foto
          </Button>
          <Button onClick={() => { setEditingInvoice(null); setDialogOpen(true); }} data-testid="new-invoice-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Factura
          </Button>
        </div>
      </div>

      {/* Drop Zone */}
      <div
        className={`upload-area ${dragOver ? "dragover" : ""}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        data-testid="drop-zone"
      >
        <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
        <p className="text-slate-600 font-medium">
          Arrastra facturas aquí (PDF o imagen) o haz clic para seleccionar
        </p>
        <p className="text-sm text-slate-500 mt-1">
          El OCR extraerá automáticamente los datos de la factura.
          Las imágenes (JPG, PNG, WEBP, HEIC) y los PDFs escaneados requieren
          las funciones de IA activadas en Ajustes.
        </p>
      </div>

      {/* Unassigned Badge */}
      {unassignedInvoices.length > 0 && (
        <button
          onClick={() => setFilterUnassigned(prev => !prev)}
          className={`w-full text-left px-4 py-2.5 rounded-lg border transition-colors ${
            filterUnassigned
              ? "bg-amber-100 border-amber-300 text-amber-800"
              : "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
          }`}
          data-testid="unassigned-badge"
        >
          <span className="flex items-center gap-2 text-sm font-medium">
            <AlertTriangle className="w-4 h-4" />
            {unassignedInvoices.length} gasto{unassignedInvoices.length !== 1 ? "s" : ""} sin asignar — {formatCurrency(unassignedEurSum)}
            {unassignedPendingReconciliation.length > 0 && (
              <span className="text-xs opacity-80">
                (+{unassignedPendingReconciliation.length} en divisa extranjera con EUR pendiente)
              </span>
            )}
            {filterUnassigned && <span className="ml-auto text-xs opacity-70">(mostrando solo sin asignar)</span>}
          </span>
        </button>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex gap-3 items-end flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label className="mb-2 block">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Proveedor o nº factura..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && fetchInvoices()}
                  className="pl-10"
                  data-testid="search-invoices-input"
                />
              </div>
            </div>
            <div className="w-44">
              <Label className="mb-2 block">Estado</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="invoice-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="accounted">Contabilizada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-56">
              <Label className="mb-2 block">Proveedor</Label>
              <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                <SelectTrigger data-testid="invoice-supplier-filter">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Todos</SelectItem>
                  {supplierOptions.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Desde</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <Label className="mb-2 block">Hasta</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <Button
              variant="outline"
              onClick={() => setSortOrder((o) => (o === "asc" ? "desc" : "asc"))}
              title={sortOrder === "asc" ? "Más antiguas primero" : "Más recientes primero"}
            >
              <ArrowUpDown className="w-4 h-4 mr-2" />
              {sortOrder === "asc" ? "Antiguas → recientes" : "Recientes → antiguas"}
            </Button>
            {(startDate || endDate || search || supplierFilter !== "all") && (
              <Button
                variant="ghost"
                onClick={() => { setStartDate(""); setEndDate(""); setSearch(""); setSupplierFilter("all"); }}
              >
                Limpiar
              </Button>
            )}
          </div>
          <div className="text-xs text-slate-500">
            Mostrando {(filterUnassigned ? unassignedInvoices : filteredBySupplier).length} de {totalInvoices} facturas
            {sortedInvoices.length >= 1000 && " (límite 1000 — usa filtros para acotar)"}
          </div>
        </CardContent>
      </Card>

      {/* Barra de acción masiva: contabilizar varias facturas pendientes a la vez */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm font-medium text-blue-900">
            {selectedIds.size} factura(s) seleccionada(s)
          </span>
          <Button size="sm" onClick={handleBulkAccount}>Contabilizar seleccionadas</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>Cancelar</Button>
        </div>
      )}

      {/* Ingesta por correo: bandeja de PDF llegados al buzón (spec 2026-07-03) */}
      {emailIngest?.enabled && (
        <Alert className="border-blue-200 bg-blue-50" data-testid="email-ingest-banner">
          <Mail className="h-4 w-4" />
          <AlertDescription className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span>
              {emailIngest.pending_documents > 0
                ? <><strong>{emailIngest.pending_documents}</strong> factura(s) llegada(s) por correo esperan tu revisión.</>
                : <>Ninguna factura del correo pendiente.</>}
              {emailIngest.last_error
                ? <span className="ml-2 text-red-700">Último intento con error: {emailIngest.last_error}</span>
                : emailIngest.last_success_at
                  ? <span className="ml-2 text-slate-500">Última comprobación: {new Date(emailIngest.last_success_at).toLocaleString("es-ES")}</span>
                  : null}
              {emailIngest.no_pdf_messages?.length > 0 && (
                <span
                  className="ml-2 text-amber-700"
                  title={emailIngest.no_pdf_messages.map((m) => `${m.sender}: ${m.subject}`).join("\n")}
                >
                  {emailIngest.no_pdf_messages.length} correo(s) sin PDF (registrar a mano)
                </span>
              )}
            </span>
            <span className="flex gap-2 ml-auto">
              {emailIngest.pending_documents > 0 && (
                <Button size="sm" onClick={handleReviewEmailDocs} data-testid="review-email-docs-btn">
                  Revisar
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={handleCheckEmailNow}
                disabled={checkingEmail}
                data-testid="check-email-now-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${checkingEmail ? "animate-spin" : ""}`} />
                {checkingEmail ? "Comprobando…" : "Comprobar correo ahora"}
              </Button>
            </span>
          </AlertDescription>
        </Alert>
      )}

      {/* Invoices Table */}
      <Card>
        <CardContent className="p-0">
          <Table className="table-dense table-fixed min-w-[1150px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={allPendingSelected}
                    onCheckedChange={toggleSelectAllPending}
                    title="Seleccionar todas las pendientes"
                  />
                </TableHead>
                <TableHead className="w-[100px]">Fecha</TableHead>
                <TableHead className="w-[220px]">Proveedor</TableHead>
                <TableHead className="w-[210px]">Nº Factura</TableHead>
                <TableHead className="text-right">Base</TableHead>
                <TableHead className="text-right">IVA</TableHead>
                <TableHead className="text-right">IRPF</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Pago</TableHead>
                <TableHead>Asignación</TableHead>
                <TableHead className="w-[180px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8">
                    <div className="spinner mx-auto" />
                  </TableCell>
                </TableRow>
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay facturas recibidas</p>
                  </TableCell>
                </TableRow>
              ) : (
                (filterUnassigned ? unassignedInvoices : filteredBySupplier).map((inv) => (
                  <TableRow key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`}>
                    <TableCell>
                      {inv.status === "pending" && (
                        <Checkbox
                          checked={selectedIds.has(inv.invoice_id)}
                          onCheckedChange={() => toggleSelect(inv.invoice_id)}
                          data-testid={`select-invoice-${inv.invoice_id}`}
                        />
                      )}
                    </TableCell>
                    <TableCell className="font-mono">
                      {formatDate(inv.invoice_date)}
                    </TableCell>
                    <TableCell className="font-medium truncate" title={inv.supplier_name}>{inv.supplier_name}</TableCell>
                    <TableCell title={inv.invoice_number}>
                      <div className="flex items-center gap-1">
                        <span className="break-all">{inv.invoice_number}</span>
                        {(inv.pdf_path || inv.document_id) && (
                          <Paperclip className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" title="Documento adjunto" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={`numeric ${inv.base_amount < 0 ? "text-rose-600" : ""}`}>
                      {isForeignInvoice(inv)
                        ? formatCurrency(inv.base_amount, inv.currency)
                        : formatCurrency(inv.base_amount)}
                    </TableCell>
                    <TableCell className={`numeric ${inv.vat_amount < 0 ? "text-rose-600" : ""}`}>
                      {isForeignInvoice(inv)
                        ? formatCurrency(inv.vat_amount, inv.currency)
                        : formatCurrency(inv.vat_amount)}
                    </TableCell>
                    <TableCell className="numeric">
                      {((inv.irpf_rate ?? 0) > 0 || (inv.irpf_amount ?? 0) !== 0) ? (
                        <span className="text-rose-600" title={`Retención IRPF ${inv.irpf_rate ?? 0}%`}>
                          −{isForeignInvoice(inv)
                            ? formatCurrency(Math.abs(inv.irpf_amount ?? 0), inv.currency)
                            : formatCurrency(Math.abs(inv.irpf_amount ?? 0))}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </TableCell>
                    <TableCell className={`numeric font-medium ${inv.total_amount < 0 ? "text-rose-600" : ""}`}>
                      {isForeignInvoice(inv) ? (
                        isReconciled(inv) ? (
                          <>
                            {formatCurrency(inv.total_amount_eur)}
                            <div className="text-xs text-slate-400 font-normal">
                              {formatCurrency(inv.original_amount ?? inv.total_amount, inv.currency)}
                            </div>
                          </>
                        ) : (
                          <>
                            {formatCurrency(inv.total_amount, inv.currency)}
                            <div className="text-xs text-amber-600 font-normal" title="El importe EUR se conocerá al conciliar con el banco">
                              EUR pendiente
                            </div>
                          </>
                        )
                      ) : (
                        formatCurrency(inv.total_amount_eur ?? inv.total_amount)
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={inv.status} />
                    </TableCell>
                    <TableCell>
                      <PaymentBadge status={inv.payment_status} />
                      {inv.payment_status === "partial" && !isForeignInvoice(inv) && (
                        <div className="text-xs text-amber-600 mt-1 whitespace-nowrap">
                          faltan {formatCurrency(inv.total_amount - (inv.amount_paid ?? 0))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <AssignmentCell
                        expenseId={inv.invoice_id}
                        totalEur={isForeignInvoice(inv) && !isReconciled(inv)
                          ? (inv.total_amount ?? 0)
                          : (inv.total_amount_eur ?? inv.total_amount ?? 0)}
                        currency={isForeignInvoice(inv) && !isReconciled(inv) ? inv.currency : "EUR"}
                        splits={expenseSplits[inv.invoice_id] || []}
                        clients={clients}
                        services={services}
                        onSave={saveSplits}
                        onDelete={deleteSplits}
                        // 3.3 — las sugerencias vienen en EUR: en facturas extranjeras
                        // sin conciliar el editor trabaja en divisa nativa, así que ahí
                        // no se pide ni se pasa sugerencia (EUR real solo tras conciliación).
                        suggestion={isForeignInvoice(inv) && !isReconciled(inv)
                          ? null
                          : (splitSuggestions[inv.invoice_id] ?? null)}
                        onOpen={isForeignInvoice(inv) && !isReconciled(inv)
                          ? undefined
                          : () => fetchSplitSuggestion(inv.invoice_id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <ReconciliationLinkDialog kind="invoice" id={inv.invoice_id} />
                        {(inv.pdf_path || inv.document_id) && (
                          <InvoicePreviewDialog
                            url={`${API}/invoices/received/${inv.invoice_id}/pdf`}
                            title={`${inv.supplier_name || "Factura"}${inv.invoice_number ? " · " + inv.invoice_number : ""}`}
                            testid={`preview-received-${inv.invoice_id}`}
                          />
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            pdfInputRef.current.dataset.invoiceId = inv.invoice_id;
                            pdfInputRef.current?.click();
                          }}
                          disabled={uploadingPdfId === inv.invoice_id}
                          title={inv.pdf_path || inv.document_id ? "Reemplazar PDF" : "Adjuntar PDF"}
                          className={inv.pdf_path || inv.document_id ? "text-blue-600" : ""}
                          data-testid={`upload-pdf-invoice-${inv.invoice_id}`}
                        >
                          {uploadingPdfId === inv.invoice_id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Paperclip className="w-4 h-4" />
                          )}
                        </Button>
                        {inv.status === "pending" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAccountInvoice(inv.invoice_id)}
                            title="Contabilizar esta factura"
                            data-testid={`account-invoice-${inv.invoice_id}`}
                            className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setEditingInvoice(inv); setDialogOpen(true); }}
                          data-testid={`edit-invoice-${inv.invoice_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteInvoice(inv.invoice_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-invoice-${inv.invoice_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Invoice Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingInvoice ? "Editar Factura" : "Nueva Factura Recibida"}
            </DialogTitle>
          </DialogHeader>
          <InvoiceForm
            invoice={editingInvoice}
            onSave={handleSaveInvoice}
            onCancel={() => { setDialogOpen(false); setEditingInvoice(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* OCR Preview Dialog — solo se cierra por botones (Cancelar / Crear) para no perder el doc al cambiar de ventana */}
      <Dialog open={ocrDialogOpen}>
        <OCRPreviewDialog
          document={currentOcrDoc}
          queueRemaining={ocrQueue.length}
          onCreateInvoice={handleCreateFromOCR}
          onClose={popOcrQueue}
          onDiscard={handleDiscardEmailDoc}
        />
      </Dialog>

    </div>
  );
}

export default function InvoicesReceived() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileInvoices initialTab="received" /> : <InvoicesReceivedDesktop />;
}
